// src/components/Ownerdashboard/OwnerLogin.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaPhoneAlt } from "react-icons/fa";
import { BadgeCheck, CheckCircle2, ListChecks, UserCheck } from "lucide-react";
import LeftPanel from "../LeftPanel";
import OwnerOtpVerify from "./OwnerOtpVerify";
import { isOwnerLoggedIn, sendOwnerOtp } from "./ownerAuth";

const OWNER_FEATURES = [
  { icon: <BadgeCheck />, title: "Free Listings" },
  { icon: <UserCheck />, title: "Verified Inquiries from Registered Brokers" },
  { icon: <ListChecks />, title: "Unlimited Inquiries" },
  { icon: <CheckCircle2 />, title: "No Extra Hassle" },
];

const OwnerLogin = ({ onSuccess }) => {
  const [step, setStep] = useState("number"); // "number" | "otp"
  const [number, setNumber] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOwnerLoggedIn()) {
      navigate("/owner/dashboard");
    }
  }, [navigate]);

  const handleNumberChange = (e) => {
    setNumber(e.target.value.replace(/\D/g, "").slice(0, 10));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!number) {
      setError("Please enter your contact number to continue.");
      return;
    }

    if (!acceptedTerms) {
      setError("Please accept the terms and conditions to proceed.");
      return;
    }

    const contactPattern = /^[0-9]{10}$/;
    if (!contactPattern.test(number)) {
      setError("Please enter a valid 10-digit phone number.");
      return;
    }

    setLoading(true);
    try {
      const result = await sendOwnerOtp(number);

      if (result.success) {
        setStep("otp");
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerified = () => {
    if (onSuccess) {
      onSuccess();
    } else {
      navigate("/owner/dashboard");
    }
  };

  return (
    <div className="flex h-screen w-full overflow-hidden">
      {/* Left panel with animations and graphics */}
      <LeftPanel
        title="List & Manage Your Property in Easy Steps"
        subtitle="40,000+ Properties Listed Across Ahmedabad"
        features={OWNER_FEATURES}
      />

      <div className="mx-4 flex items-center lg:mx-28 mb-28">
        <div className="mt-10 w-full max-w-lg flex-1 rounded-lg">
          {step === "otp" ? (
            <OwnerOtpVerify
              number={number}
              onVerified={handleVerified}
              onChangeNumber={() => setStep("number")}
            />
          ) : (
            <form onSubmit={handleSubmit}>

              <p className=" text-3xl font-bold">Manage Your Property, </p>
              <p className="mb-8 text-3xl font-bold">Owner Login !</p>

              <div className="relative mb-4">
                <FaPhoneAlt className="absolute left-4 top-1/2 -translate-y-1/2 transform text-[#503691]" />
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter Your Contact Number"
                  value={number}
                  onChange={handleNumberChange}
                  className="no-spinner w-full rounded-md border border-gray-300 px-10 py-3"
                  required
                />
              </div>

              {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-center text-red-600 shadow-sm">
                  <div className="flex items-center justify-center gap-2">
                    <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="font-medium">{error}</span>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 py-3">
                <input
                  type="checkbox"
                  id="owner-terms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="h-7 w-7 cursor-pointer rounded border-gray-300 text-[#503691] focus:ring-[#503691]"
                />
                <label
                  htmlFor="owner-terms"
                  className="text-center text-[13px] text-gray-700 lg:text-[15px]"
                >
                  By clicking on login button you're accepting our{" "}
                  <Link to="/terms-of-service" className="text-blue-500 hover:underline">
                    Terms and Conditions
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy-policy" className="text-blue-500 hover:underline">
                    Privacy Policy
                  </Link>
                </label>
              </div>

              <button
                type="submit"
                className="h-11 w-full rounded-full bg-[#503691] font-semibold text-white hover:bg-[#5b54e6] disabled:cursor-not-allowed disabled:opacity-50"
                disabled={loading || !acceptedTerms}
              >
                {loading ? "Sending OTP..." : "Login"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default OwnerLogin;
